import PropTypes from 'prop-types'

import {
	TicketImportanceOptions,
	TicketStatusOptions,
	TicketTypeOptions,
	TicketUrgencyOptions
} from '../../../../shared/const'

import { roleLabel } from '../../../../shared/helpers'

import { PlainSelect } from '../../../../UI/PlainField'

import styles from './detail.module.scss'

/**
 * Die Kopffelder einer Anforderung.
 *
 * Bewusst eine einzige Stelle für beide Fenster: Das Erfassen und das Öffnen
 * einer Anforderung zeigten dieselben Angaben in unterschiedlicher Reihenfolge
 * und unterschiedlichem Aufbau — wer eine Anforderung anlegte und sie danach
 * öffnete, sah zwei verschiedene Formulare. Kommt ein Feld dazu, taucht es
 * jetzt an beiden Stellen gleich auf.
 *
 * Die Art steht vorne: Sie entscheidet, worum es geht. Wem die Anforderung
 * gehört, steht daneben — die Zuweisung ist unabhängig von der Art.
 *
 * `onChange(feld, wert)` statt einzelner Rückrufe je Feld: Das Detailfenster
 * schickt jede Änderung sofort an den Server, das Erfassungsfenster sammelt sie
 * bis zum Speichern. Beides ist dieselbe Bewegung, nur mit anderem Ziel.
 *
 * `zeile` wählt aus, welche der beiden Reihen gezeigt wird — das
 * Erfassungsfenster zeigt beide nacheinander.
 *
 * `spalte` ist die dritte Form: alle sechs Felder untereinander, für die
 * Seitenspalte des Detailfensters. Dort standen sie bis 22.08.2026 an zwei
 * Orten (drei über den Reitern, drei im Reiter „Einstufung") — sechs Angaben,
 * die zusammengehören, getrennt durch einen Reiterwechsel. Jetzt stehen sie
 * immer daneben, gleich welcher Reiter offen ist.
 */
export default function TicketFields({
	values,
	projects,
	assignees,
	onChange,
	disabled,
	zeile,
	spalte
}) {
	const projectOptions = projects.map(project => ({
		value: project.id,
		label: project.title
	}))

	/*
	 * Name und Rolle in Klammern. Es ist ein einfaches Auswahlfeld des Browsers
	 * — dort ist ein Schild nicht möglich, und „Landesadmin" allein sagt bei
	 * neun Bundesländern zu wenig, der Name allein nicht, wer zuständig ist.
	 */
	const assigneeOptions = assignees.map(person => {
		const name = person.name || person.email
		const rolle = roleLabel(person.role)
		return { value: person.id, label: rolle ? `${name} (${rolle})` : name }
	})

	// Leere Auswahl heißt "keins" bzw. "niemand" — nicht 0. Die 0 ist nur die
	// Filter-Konvention der Liste.
	const zahlOderNull = wert => (wert === '' ? null : Number(wert))

	const zeigeKopf = spalte || zeile !== 'einordnung'
	const zeigeEinordnung = spalte || zeile !== 'kopf'

	// In der Spalte steht jedes Feld für sich untereinander; in der Zeile liegen
	// je drei nebeneinander. Dieselben Felder, nur ein anderer Kasten darum.
	const gitter = spalte ? styles.fieldColumn : styles.fieldGrid
	const rahmen = spalte ? styles.fieldStack : zeigeKopf && zeigeEinordnung ? styles.fieldBlock : styles.fieldRow

	return (
		<div className={rahmen}>
			{zeigeKopf && (
				<div className={gitter}>
					<PlainSelect
						label="Art"
						value={values.type}
						options={TicketTypeOptions}
						disabled={disabled}
						onChange={event => onChange('type', event.target.value)}
					/>
					{/*
					 * Dieselbe Kette für jede Art (seit 22.08.2026). Vorher hing die
					 * Auswahl an der Art, und ein Artwechsel schrieb den Status still
					 * um — die Umschlüsselung ist mit der zweiten Kette entfallen.
					 */}
					<PlainSelect
						label="Status"
						value={values.status}
						options={TicketStatusOptions}
						disabled={disabled}
						onChange={event => onChange('status', event.target.value)}
					/>
					<PlainSelect
						label="Zugewiesen an"
						value={values.assignedTo ?? ''}
						placeholder="Niemandem zugewiesen"
						options={assigneeOptions}
						disabled={disabled}
						onChange={event => onChange('assignedTo', zahlOderNull(event.target.value))}
					/>
				</div>
			)}

			{zeigeEinordnung && (
				<div className={gitter}>
					<PlainSelect
						label="Dringlichkeit"
						value={values.urgency}
						options={TicketUrgencyOptions}
						disabled={disabled}
						onChange={event => onChange('urgency', Number(event.target.value))}
					/>
					<PlainSelect
						label="Wichtigkeit"
						value={values.importance}
						options={TicketImportanceOptions}
						disabled={disabled}
						onChange={event => onChange('importance', Number(event.target.value))}
					/>
					<PlainSelect
						label="Projekt"
						value={values.projectId ?? ''}
						placeholder="Ohne Projekt"
						options={projectOptions}
						disabled={disabled}
						onChange={event => onChange('projectId', zahlOderNull(event.target.value))}
					/>
				</div>
			)}
		</div>
	)
}

TicketFields.propTypes = {
	values: PropTypes.shape({
		type: PropTypes.string,
		status: PropTypes.string,
		assignedTo: PropTypes.number,
		urgency: PropTypes.number,
		importance: PropTypes.number,
		projectId: PropTypes.number
	}).isRequired,
	projects: PropTypes.array,
	assignees: PropTypes.array,
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	zeile: PropTypes.oneOf(['beide', 'kopf', 'einordnung']),
	// Alle sechs Felder untereinander, fuer die Seitenspalte des Detailfensters.
	// Setzt `zeile` ausser Kraft.
	spalte: PropTypes.bool
}

TicketFields.defaultProps = {
	projects: [],
	assignees: [],
	disabled: false,
	zeile: 'beide',
	spalte: false
}
